import * as React from "react";
import { logger } from "@/utils/logger";

/**
 * The z-index of the dialog a piece of UI is drawn in, so a layer it opens can
 * be stacked above it. Popovers portal to <body> at the shared `z-50`; a dialog
 * raised over another (a file viewer at `z-[60]` over Organise folders, a
 * dialog in the 3D preview at `z-[70]`) would otherwise hide every popover
 * opened from inside it. React context crosses the portal where the DOM does
 * not.
 *
 * The value comes from the dialog's own `className`/`style`, not from the DOM:
 * it is known on the dialog's first render, so a popover that opens in the same
 * commit as its dialog is stacked correctly too.
 */
const LayerHostContext = React.createContext<number | undefined>(undefined);

/** The z every portalled layer sits at unless its className says otherwise. */
const BASE_LAYER_Z = 50;

/** No z-index utility at all, as opposed to one this parser cannot read. */
const NO_Z_CLASS = Symbol("no z class");

/** `z-10`, `z-[60]`, `-z-10`, `!z-50`; groups: important, minus, arbitrary, scale. */
const PLAIN_Z_CLASS = /^(!)?(-)?z-(?:\[(-?\d+)\]|(\d+))$/;

/**
 * The z-index the `z-*` utilities in `className` ask for. `NO_Z_CLASS` when
 * there are none; `undefined` when the answer depends on something this cannot
 * see — a variant (`sm:z-[70]`, `focus:z-10`) applies only in some states,
 * `z-auto` sets no number, and two plain tokens with different values are
 * resolved by stylesheet order, not by their order in the string — so a caller
 * never has a z-index decided for them that their class would not have given.
 */
function classZIndex(className: string | undefined): number | undefined | typeof NO_Z_CLASS {
  // A z utility starts the token or follows a variant's colon; `[--z-gap:2px]`
  // merely contains `-z-` and is not one.
  const tokens = (className ?? "").split(/\s+/).filter((t) => /(^|:)!?-?z-/.test(t));
  if (tokens.length === 0) return NO_Z_CLASS;
  const values = new Set<number>();
  for (const token of tokens) {
    const match = token.match(PLAIN_Z_CLASS);
    if (!match) return undefined;
    const z = Number(match[3] ?? match[4]);
    values.add(match[2] ? -z : z);
  }
  return values.size === 1 ? [...values][0] : undefined;
}

/** Does `className` set its z-index with `!important`, which no inline value beats? */
const hasImportantZClass = (className: string | undefined) =>
  (className ?? "").split(/\s+/).some((t) => PLAIN_Z_CLASS.exec(t)?.[1] === "!");

/**
 * In development, says when a host's z-index utility cannot be read, so the
 * layers it opens staying at z-50 is not a silent failure. Once per mount and
 * className, from an effect rather than render, so nothing is cached.
 */
function useWarnUnreadableZ(className: string | undefined, style: React.CSSProperties | undefined) {
  const unreadable = inlineZIndex(style) === undefined && classZIndex(className) === undefined;
  React.useEffect(() => {
    if (!import.meta.env.DEV || !unreadable || !className) return;
    logger.warnLocal(
      `[layer] Cannot read the z-index utility in "${className}". Layers opened inside will ` +
        "stay at z-50 and may paint behind it; use a plain z-N / z-[N] class or an inline zIndex."
    );
  }, [unreadable, className]);
}

/** An inline `zIndex` that is a number; `auto`, `""` and the like are not. */
function inlineZIndex(style: React.CSSProperties | undefined): number | undefined {
  const inline = style?.zIndex;
  const z = typeof inline === "string" ? Number.parseInt(inline, 10) : inline;
  return typeof z === "number" && Number.isFinite(z) ? z : undefined;
}

/** A layer's z-index from its props, or `undefined` if unknown. */
function hostZIndex(className: string | undefined, style: React.CSSProperties | undefined) {
  const inline = inlineZIndex(style);
  if (inline !== undefined) return inline;
  const z = classZIndex(className);
  return z === NO_Z_CLASS ? BASE_LAYER_Z : z;
}

/**
 * Wraps a dialog's children so the layers they open can clear it. A dialog
 * opened from inside a raised one is drawn over it, so the higher of the two
 * is what its layers must clear.
 */
export function LayerHost({
  className,
  style,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  useWarnUnreadableZ(className, style);
  const inherited = React.useContext(LayerHostContext);
  const own = hostZIndex(className, style);
  // A z this cannot read keeps the level it was opened at rather than none.
  const z =
    own === undefined ? inherited : inherited === undefined ? own : Math.max(inherited, own);
  return <LayerHostContext.Provider value={z}>{children}</LayerHostContext.Provider>;
}

/**
 * A z-index that clears the enclosing dialog, or `undefined` when the layer's
 * own `className` z (the shared `z-50`, or a caller's hand-tuned `z-[70]`)
 * already does — an inline value would beat the caller's class — or when the
 * caller's z is one this cannot read, or one they set inline themselves.
 */
export function useLayerAboveHost(
  className?: string,
  style?: React.CSSProperties
): number | undefined {
  const hostZ = React.useContext(LayerHostContext);
  const raised = hostZ != null && hostZ > BASE_LAYER_Z;
  const own = classZIndex(className);
  // Inside a raised host, a z left to the caller that this cannot read or
  // beat may paint behind it; say so in development rather than silently.
  const unraisable =
    raised &&
    inlineZIndex(style) === undefined &&
    (own === undefined || hasImportantZClass(className));
  React.useEffect(() => {
    if (!import.meta.env.DEV || !unraisable) return;
    logger.warnLocal(
      `[layer] Cannot raise a layer with "${className}" above the dialog it opens from ` +
        `(z ${hostZ}); it may paint behind it. Use a plain z-N / z-[N] class or an inline zIndex.`
    );
  }, [unraisable, className, hostZ]);
  if (!raised || hostZ == null) return undefined;
  if (inlineZIndex(style) !== undefined || hasImportantZClass(className)) return undefined;
  if (own === undefined) return undefined;
  // A negative z (`-z-10`) puts a layer behind on purpose; it stays there.
  if (own !== NO_Z_CLASS && own < 0) return undefined;
  return hostZ >= (own === NO_Z_CLASS ? BASE_LAYER_Z : own) ? hostZ + 1 : undefined;
}

/**
 * Everything a portalled layer (popover, select, menu, tooltip, hover card)
 * needs to stack above the dialog it is opened from, and to let the layers it
 * opens in turn stack above it:
 *
 * - `style` carries the raised z-index, when one is needed.
 * - `ref` must go on the primitive's Content. Radix's popper copies the
 *   content's z-index onto its positioned wrapper — the element that actually
 *   competes for stacking — once, when it mounts; if the host's z changes while
 *   the layer is open, the wrapper is updated here.
 * - `z` is this layer's own z-index, for `<LayerScope>` around its children.
 */
export function useRaisedLayer<T extends HTMLElement>(
  className: string | undefined,
  style: React.CSSProperties | undefined,
  forwardedRef: React.ForwardedRef<T>
) {
  const raisedZ = useLayerAboveHost(className, style);
  const nodeRef = React.useRef<T | null>(null);
  const ref = React.useCallback(
    (node: T | null) => {
      nodeRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );
  // What the wrapper's z depends on: the raise, and the layer's own z (a
  // className that adds `z-[70]` in a preview).
  const syncKey = `${raisedZ ?? ""}|${className ?? ""}|${style?.zIndex ?? ""}`;
  const syncedKey = React.useRef<string | null>(null);
  React.useLayoutEffect(() => {
    const node = nodeRef.current;
    const wrapper = node?.parentElement;
    if (!node || !wrapper || wrapper === document.body) return;
    if (raisedZ != null) {
      // Checked after every render, since Radix renders the wrapper's z from
      // state of its own. The raise is inline and never fights an important
      // class, so it IS the content's z — no style read needed.
      const z = String(raisedZ);
      if (wrapper.style.zIndex !== z) wrapper.style.zIndex = z;
    } else if (syncedKey.current !== syncKey) {
      // Unraised, the computed z is read only when its inputs change.
      wrapper.style.zIndex = window.getComputedStyle(node).zIndex;
    }
    syncedKey.current = syncKey;
  });
  return {
    ref,
    // The raise goes last: a caller's `zIndex: undefined` or `"auto"` must not
    // erase it (a numeric one stops the raise before it gets here).
    style: raisedZ == null ? style : { ...style, zIndex: raisedZ },
    z: raisedZ ?? hostZIndex(className, style),
  };
}

/**
 * The raise for a dialog, which is not inside a popper wrapper, opened from
 * inside a raised layer (a dialog launched from a popover in a `z-[60]` dialog
 * would otherwise sit at z-50, under the popover that opened it).
 *
 * The backdrop follows the content's raise and nothing else: raised on its own
 * it could land above a dialog whose z is left to the caller (`!z-50`, an
 * inline zIndex), covering it so it can be neither used nor dismissed. At the
 * same z it still paints underneath, being first in DOM order.
 */
export function useRaisedDialog(
  className: string | undefined,
  style: React.CSSProperties | undefined,
  overlayClassName?: string
): { content: React.CSSProperties | undefined; overlay: React.CSSProperties | undefined } {
  const raisedZ = useLayerAboveHost(className, style);
  // The backdrop takes the raised content's z: higher would cover the dialog
  // it backs, lower would leave the host undimmed. A caller's own backdrop z
  // cannot hold, so say so rather than drop it silently.
  const overlayOwn = classZIndex(overlayClassName);
  const overlayOverridden =
    raisedZ != null && typeof overlayOwn === "number" && overlayOwn !== raisedZ;
  React.useEffect(() => {
    if (!import.meta.env.DEV || !overlayOverridden) return;
    logger.warnLocal(
      `[layer] The backdrop class "${overlayClassName}" is overridden: a dialog raised above the layer it opens from (z ${raisedZ}) needs its backdrop at the same z.`
    );
  }, [overlayOverridden, overlayClassName, raisedZ]);
  if (raisedZ == null) return { content: style, overlay: undefined };
  return { content: { ...style, zIndex: raisedZ }, overlay: { zIndex: raisedZ } };
}

const scrollable = (overflow: string) => overflow === "auto" || overflow === "scroll";

/**
 * Is there something between `target` and the layer that can scroll, on
 * either axis? Only then does a wheel/touch-move need saving from the lock.
 */
function scrollsWithin(target: EventTarget, layer: Element): boolean {
  // A layer portalled from inside this one (a Select, a tooltip) bubbles its
  // React events through here but is not in this DOM subtree; it keeps its own
  // scroll handling.
  if (!(target instanceof Element) || !layer.contains(target)) return false;
  for (let el: Element | null = target; el && el !== layer.parentElement; el = el.parentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(el);
    if (scrollable(overflowY) && el.scrollHeight > el.clientHeight) return true;
    if (scrollable(overflowX) && el.scrollWidth > el.clientWidth) return true;
  }
  return false;
}

/**
 * Wraps a layer's `onWheel`/`onTouchMove` so a native wheel/touch-move over a
 * scrollable part of it does not bubble to `document`, where an open Radix
 * Dialog's `react-remove-scroll` listener cancels any scroll whose target is
 * outside the dialog's DOM — which a portalled layer always is
 * (radix-ui/primitives#1159). The browser's own scrolling is not prevented.
 * Anywhere else the event goes on as normal, so the lock still stops the page
 * behind from scrolling. A modal menu or Select takes the lock over with one
 * that admits its own content; a popover or a `modal={false}` menu does not.
 */
export function keepScrollInLayer<E extends React.SyntheticEvent>(
  handler: ((event: E) => void) | undefined
) {
  return (event: E) => {
    handler?.(event);
    if (scrollsWithin(event.target, event.currentTarget)) event.stopPropagation();
  };
}

/**
 * Makes `z` the host for layers opened from inside a layer. Like `LayerHost`,
 * a z this cannot read keeps the level the layer was opened at.
 */
export function LayerScope({ z, children }: { z: number | undefined; children: React.ReactNode }) {
  const inherited = React.useContext(LayerHostContext);
  const value = z === undefined ? inherited : inherited === undefined ? z : Math.max(inherited, z);
  return <LayerHostContext.Provider value={value}>{children}</LayerHostContext.Provider>;
}
